// src/screens/MetodosPagoScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, functions } from '../firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { IoArrowBack, IoCardOutline, IoAddOutline, IoTrashOutline, IoCheckmarkCircle } from 'react-icons/io5';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripeSetupForm from '../components/StripeSetupForm';
import './MetodosPagoScreen.css';

// Reusa la llave publicable del entorno de Módulo de Pagos
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!stripeKey) {
  console.warn("⚠️ REACT_APP_STRIPE_PUBLISHABLE_KEY no está definida en las variables de entorno.");
}
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

function MetodosPagoScreen() {
    const navigate = useNavigate();
    const { currentUser, userData, refreshUserData } = useAuth();
    
    const [stripeCustomerId, setStripeCustomerId] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [domiciliacionActiva, setDomiciliacionActiva] = useState(false);
    const [metodoPredeterminado, setMetodoPredeterminado] = useState(null);
    
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [clientSecret, setClientSecret] = useState('');
    
    // Obtenemos los datos iniciales
    useEffect(() => {
        const fetchPaymentData = async () => {
            if (!currentUser) return;
            setLoading(true);
            try {
                // 1. Obtener o Crear Customer en Stripe
                const getCustomerParams = httpsCallable(functions, 'getStripeCustomer');
                const resCustomer = await getCustomerParams();
                const sId = resCustomer.data.stripeCustomerId;
                setStripeCustomerId(sId);

                // 2. Fetch de datos locales en Firestore (Domiciliación)
                const userRef = doc(db, 'usuarios', currentUser.uid);
                const userDoc = await getDoc(userRef);
                if (userDoc.exists()) {
                    setDomiciliacionActiva(userDoc.data().domiciliacionActiva || false);
                    setMetodoPredeterminado(userDoc.data().metodoPagoPredeterminado || null);
                }

                // 3. Listar Tarjetas
                const listPMs = httpsCallable(functions, 'listPaymentMethods');
                const resList = await listPMs({ stripeCustomerId: sId });
                setPaymentMethods(resList.data.paymentMethods || []);

            } catch (error) {
                console.error("Error al cargar datos de pago:", error);
                alert("Hubo un error cargando tus métodos de pago.");
            }
            setLoading(false);
        };

        fetchPaymentData();
    }, [currentUser]);

    // UI HANDLERS

    const handleAddCardClick = async () => {
        if (!stripeCustomerId) return alert("Falta configuración de cliente. Intenta recargar la página.");
        
        try {
            const createSI = httpsCallable(functions, 'createSetupIntent');
            const resSI = await createSI({ stripeCustomerId });
            setClientSecret(resSI.data.clientSecret);
            setShowAddModal(true);
        } catch (error) {
            console.error(error);
            alert("No se pudo iniciar el proceso de guardado de tarjeta.");
        }
    };

    const handleSetupSuccess = async () => {
        setShowAddModal(false);
        setClientSecret('');
        setLoading(true);
        
        // Recargar las tarjetas
        try {
            const listPMs = httpsCallable(functions, 'listPaymentMethods');
            const resList = await listPMs({ stripeCustomerId });
            const pms = resList.data.paymentMethods || [];
            setPaymentMethods(pms);

            // Si es la primera tarjeta, hacerla predeterminada
            if (pms.length === 1) {
                await setPredeterminado(pms[0].id);
            }
        } catch (error) {
            console.error(error);
        }
        setLoading(false);
    };

    const handleRemoveCard = async (pmId) => {
        if (!window.confirm("¿Seguro que deseas eliminar esta tarjeta?")) return;
        setLoading(true);
        try {
            const removePM = httpsCallable(functions, 'removePaymentMethod');
            await removePM({ paymentMethodId: pmId, stripeCustomerId });
            
            setPaymentMethods(prev => prev.filter(pm => pm.id !== pmId));
            
            // Si eliminó la predeterminada, apagar domiciliación
            if (metodoPredeterminado === pmId) {
                setMetodoPredeterminado(null);
                setDomiciliacionActiva(false);
            }
        } catch (error) {
            console.error(error);
            alert("Ocurrió un error al eliminar la tarjeta.");
        }
        setLoading(false);
    };

    const handleToggleDomiciliacion = async () => {
        if (!domiciliacionActiva && paymentMethods.length === 0) {
            return alert("Debes agregar al menos una tarjeta antes de activar el cobro automático.");
        }
        if (!domiciliacionActiva && !metodoPredeterminado && paymentMethods.length > 0) {
           await setPredeterminado(paymentMethods[0].id); // Auto set first if none
        }

        const newState = !domiciliacionActiva;
        setDomiciliacionActiva(newState);
        
        try {
            await updateDoc(doc(db, 'usuarios', currentUser.uid), {
                domiciliacionActiva: newState
            });
            refreshUserData();
        } catch (e) {
            console.error(e);
            alert("Error al actualizar la configuración de cobro.");
        }
    };

    const setPredeterminado = async (pmId) => {
        setMetodoPredeterminado(pmId);
        try {
            await updateDoc(doc(db, 'usuarios', currentUser.uid), {
                metodoPagoPredeterminado: pmId
            });
            refreshUserData();
        } catch (e) {
            console.error(e);
        }
    };

    // RENDER HELPERS
    const getCardLogo = (brand) => {
        switch (brand) {
            case 'visa': return "💳 Visa";
            case 'mastercard': return "💳 MC";
            case 'amex': return "💳 Amex";
            default: return "💳 Tarjeta";
        }
    };

    if (loading) {
        return <div style={{ padding: 40, textAlign: 'center' }}>Cargando información de pagos...</div>;
    }

    const appearance = { theme: 'stripe' };
    const options = { clientSecret, appearance };

    return (
        <div className="metodos-pago-container">
            {/* Premium Header Banner */}
            <div style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', position: 'relative', overflow: 'hidden', padding: '25px', borderRadius: '15px', marginBottom: '30px', boxShadow: '0 4px 15px rgba(109, 40, 217, 0.2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', zIndex: 2, position: 'relative' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <button type="button" onClick={() => navigate('/perfil')} style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(5px)', border: 'none', borderRadius: '50%', width: '42px', height: '42px', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', transition: 'background 0.2s', color: 'white', flexShrink: 0 }}>
                            <IoArrowBack size={22} />
                        </button>
                        <div style={{ marginTop: '0' }}>
                            <h1 style={{ color: 'white', margin: '0 0 5px', fontSize: '1.6rem', fontWeight: '800' }}>Métodos de Pago</h1>
                            <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, fontSize: '0.9rem', fontWeight: '500' }}>Administra tus tarjetas y cobros seguros</p>
                        </div>
                    </div>
                </div>
                {/* Decoración de fondo */}
                <div style={{ position: 'absolute', right: '-25px', bottom: '-40px', zIndex: 0, pointerEvents: 'none', transform: 'rotate(-10deg)', opacity: 0.15 }}>
                    <IoCardOutline size={180} color="white" />
                </div>
            </div>

            {/* CAJA DE DOMICILIACIÓN */}
            <div className="domiciliacion-box">
                <div className="domiciliacion-info">
                    <h3>Cobro Automático (Domiciliación)</h3>
                    <p>
                        Tu cuota mensual de mantenimiento y cargos fijos se cargarán automáticamente a tu tarjeta predeterminada para evitar recargos.
                    </p>
                </div>
                <label className="mp-toggle-switch">
                    <input 
                        type="checkbox" 
                        checked={domiciliacionActiva} 
                        onChange={handleToggleDomiciliacion} 
                    />
                    <span className="mp-slider"></span>
                </label>
            </div>

            {/* LISTA DE TARJETAS */}
            <h3 style={{ margin: '0 0 10px 0', color: '#334155' }}>Mis Tarjetas</h3>
            
            {paymentMethods.length === 0 ? (
                <div className="no-cards">
                    <IoCardOutline size={50} style={{ marginBottom: 10, opacity: 0.5 }} />
                    <p>No tienes tarjetas guardadas.</p>
                </div>
            ) : (
                <div className="tarjetas-grid">
                    {paymentMethods.map(pm => {
                        const isDefault = pm.id === metodoPredeterminado;
                        return (
                            <div key={pm.id} className={`tarjeta-item ${isDefault ? 'predeterminada' : ''}`}>
                                {isDefault && <span className="badge-predeterminada"><IoCheckmarkCircle style={{marginRight: 4, transform: 'translateY(1px)'}}/> Predeterminada</span>}
                                
                                <div className="tarjeta-info">
                                    <div className="tarjeta-brand">
                                        {/* Icon Placeholder based on brand */}
                                        <b style={{fontSize: '1.2rem', color: '#1e293b'}}>{getCardLogo(pm.card.brand)}</b>
                                    </div>
                                    <div className="tarjeta-details">
                                        <p className="tarjeta-number">•••• {pm.card.last4}</p>
                                        <p className="tarjeta-exp">Vence {pm.card.exp_month}/{pm.card.exp_year}</p>
                                    </div>
                                </div>

                                <div className="tarjeta-actions">
                                    {!isDefault && (
                                        <button className="tarjeta-action-btn" onClick={() => setPredeterminado(pm.id)}>
                                            Hacer predet.
                                        </button>
                                    )}
                                    <button className="tarjeta-action-btn delete" onClick={() => handleRemoveCard(pm.id)}>
                                        <IoTrashOutline />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <button className="mp-add-btn" onClick={handleAddCardClick}>
                <IoAddOutline size={20} /> Agregar Nueva Tarjeta
            </button>


            {/* MODAL DE STRIPE SETUP INTENT */}
            {showAddModal && clientSecret && (
                <div className="mp-modal-overlay" onClick={() => setShowAddModal(false)}>
                    <div className="mp-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="mp-modal-header">
                            <h2>Guardar Tarjeta Segura</h2>
                        </div>
                        
                        <Elements options={options} stripe={stripePromise}>
                            <StripeSetupForm 
                                onSuccess={handleSetupSuccess} 
                                onCancel={() => setShowAddModal(false)}
                                clientSecret={clientSecret}
                            />
                        </Elements>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MetodosPagoScreen;
